import { useContext, useRef } from "react";
import { Chart } from "chart.js/auto";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import TypesContext from "../../context/TypesContext";
import { IndustryType } from "../../services/types";
import { backgroundColorPlugin, noDataPlugin } from "../../utils/chartPlugins";

interface ActivitiesBySectorChartProps {
    industryCounts: { [industryName: string]: number };
    loading?: boolean;
}

function ActivitiesBySectorChart({ industryCounts }: ActivitiesBySectorChartProps) {
    Chart.register(ChartDataLabels, noDataPlugin, backgroundColorPlugin);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    const { typesData } = useContext(TypesContext);
    const industryTypes: IndustryType[] = typesData.industry_types;

    useDeepCompareEffect(() => {
        if (!industryCounts || Object.keys(industryCounts).length === 0) return;

        // Get unique sector categories from industryTypes
        const categories = Array.from(
            new Set(industryTypes.map(ind => ind.category).filter(Boolean))
        );

        // Initialize sector totals by category
        const sectorTotals: { [key: string]: number } = {};
        categories.forEach(cat => sectorTotals[cat] = 0);

        // Map industry name → category
        const industryToCategory: { [industryName: string]: string } = {};
        industryTypes.forEach(ind => {
            if (ind.name && ind.category) {
                industryToCategory[ind.name] = ind.category;
            }
        });

        // Distribute counts from industryCounts into categories
        Object.entries(industryCounts).forEach(([industryName, count]) => {
            const category = industryToCategory[industryName] || "Other";
            if (!sectorTotals[category]) sectorTotals[category] = 0;
            sectorTotals[category] += count;
        });

        const labels = Object.keys(sectorTotals);
        const data = Object.values(sectorTotals);

        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels,
                    datasets: [
                        {
                            label: "Activities by Sector",
                            data,
                            backgroundColor: '#4D4D4D',
                            borderRadius: 3,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    indexAxis: 'y',
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: "#FFFFFF",
                            titleColor: "#4D4D4D",
                            bodyColor: "#4D4D4D",
                            borderColor: "#4D4D4D",
                            borderWidth: 1,
                            titleFont: { family: 'Gotham' },
                            bodyFont: { family: 'Gotham' }
                        },
                        datalabels: {
                            color: '#4D4D4D',
                            anchor: 'end',
                            align: 'end',
                            font: { family: 'Gotham', size: 10 },
                            offset: 5,
                            formatter: (value) => value !== 0 ? value : null
                        },
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            ticks: {
                                color: '#4D4D4D',
                                precision: 0,
                                font: { family: 'Gotham', size: 10 },
                                padding: 10
                            },
                            grid: { tickBorderDash: [2, 4] },
                            border: { display: false, dash: [2,4] }
                        },
                        y: {
                            grid: { display: false, drawTicks: true },
                            border: { display: false },
                            ticks: {
                                color: '#4D4D4D',
                                autoSkip: false,
                                font: { family: 'Gotham', size: 10 }
                            },
                        },
                    },
                },
                plugins: [noDataPlugin, backgroundColorPlugin],
            });
        }
    }, [industryCounts, typesData]);

    return (
        <div className="chart-container w-full h-84 min-w-lg max-w-lg border border-solid border-lightGray rounded-md appearance-none bg-at-white">
            <h2 className="text-white text-base font-bold py-2 px-3 bg-gotham-gray rounded-t-md">Interactions by Sector</h2>
            <div className="flex items-center border-t" />
            <canvas ref={chartRef} className="max-h-72 max-w-md mx-8 mt-12" />
        </div>
    );
}

export default ActivitiesBySectorChart;
